/** Zero-dependency WebAuthn helpers: base64url <-> ArrayBuffer and option/credential JSON conversion. */

export function b64urlToBuf(s: string): ArrayBuffer {
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function bufToB64url(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window && typeof navigator.credentials?.create === "function";
}

/**
 * Passkey autofill ("conditional mediation") offers saved passkeys inside the
 * sign-in field rather than in a modal the person has to ask for, and it is the
 * path a credential manager extension can serve. It needs a field marked
 * autocomplete="… webauthn" and an engine that implements it.
 */
export async function passkeyAutofillAvailable(): Promise<boolean> {
  const pkc = typeof window === "undefined" ? undefined : (window.PublicKeyCredential as unknown as { isConditionalMediationAvailable?: () => Promise<boolean> } | undefined);
  try {
    return (await pkc?.isConditionalMediationAvailable?.()) === true;
  } catch {
    return false;
  }
}

interface CreationJSON {
  challenge: string;
  rp: { name: string; id?: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  timeout?: number;
  excludeCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}
interface RequestJSON {
  challenge: string;
  rpId?: string;
  timeout?: number;
  allowCredentials?: Array<{ type: "public-key"; id: string; transports?: string[] }>;
  userVerification?: UserVerificationRequirement;
}

/**
 * The api pairs every set of options with a one-shot server-side challenge id
 * and nests the browser's options under `publicKey`. Verification consumes that
 * id, so the two travel together rather than as separate arguments.
 */
export interface CreationOptions {
  challengeId: string;
  publicKey: CreationJSON;
}
export interface RequestOptions {
  challengeId: string;
  publicKey: RequestJSON;
}
export interface PasskeyResult {
  challengeId: string;
  credential: Record<string, unknown>;
}

/**
 * Dismissing the platform sheet is a choice, not a failure, so it gets its own
 * type: callers can offer the alternative factor instead of showing an alarm.
 */
export class PasskeyCancelledError extends Error {
  constructor() {
    super("Passkey prompt was dismissed");
    this.name = "PasskeyCancelledError";
  }
}

/**
 * WebAuthn rejects with DOMExceptions whose messages are spec prose, e.g. "The
 * operation either timed out or was not allowed. See: https://www.w3.org/TR/…".
 * Never surface those. Cancellation and timeout are indistinguishable by design
 * (privacy), so both become PasskeyCancelledError.
 */
function passkeyError(e: unknown, action: "register" | "login"): Error {
  const name = e instanceof DOMException ? e.name : "";
  if (name === "NotAllowedError" || name === "AbortError") return new PasskeyCancelledError();
  switch (name) {
    case "InvalidStateError":
      return new Error(
        action === "register"
          ? "This device already has a passkey for your account. Sign in with it, or add a passkey from another device."
          : "That passkey is no longer registered. Sign in another way, then add a new one.",
      );
    case "NotSupportedError":
      return new Error("This device cannot create a passkey. Use an authenticator app instead.");
    case "ConstraintError":
      return new Error("This device cannot store a passkey that meets our security requirements. Try another device, or use an authenticator app.");
    case "SecurityError":
      return new Error("Passkeys need a secure page whose address matches this site. Tell your administrator if this keeps happening.");
    default:
      return new Error(action === "register" ? "Could not create a passkey on this device." : "Could not sign in with a passkey.");
  }
}

export async function createPasskey(options: CreationOptions): Promise<PasskeyResult> {
  const o = options.publicKey;
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...o,
    challenge: b64urlToBuf(o.challenge),
    user: { ...o.user, id: b64urlToBuf(o.user.id) },
    excludeCredentials: o.excludeCredentials?.map((c) => ({ ...c, id: b64urlToBuf(c.id), transports: c.transports as AuthenticatorTransport[] | undefined })),
  };
  let cred: PublicKeyCredential | null;
  try {
    cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  } catch (e) {
    throw passkeyError(e, "register");
  }
  if (!cred) throw new PasskeyCancelledError();
  const att = cred.response as AuthenticatorAttestationResponse;
  return {
    challengeId: options.challengeId,
    credential: {
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: { clientDataJSON: bufToB64url(att.clientDataJSON), attestationObject: bufToB64url(att.attestationObject) },
      clientExtensionResults: cred.getClientExtensionResults(),
    },
  };
}

/**
 * `autofill` requests conditional mediation, which resolves only if the person
 * picks a passkey from the field's dropdown. Pass a signal so a later modal
 * request can cancel it: the browser rejects a second get() while one is open.
 */
export async function getPasskey(options: RequestOptions, autofill?: { signal: AbortSignal }): Promise<PasskeyResult> {
  const o = options.publicKey;
  const publicKey: PublicKeyCredentialRequestOptions = {
    ...o,
    challenge: b64urlToBuf(o.challenge),
    allowCredentials: o.allowCredentials?.map((c) => ({ ...c, id: b64urlToBuf(c.id), transports: c.transports as AuthenticatorTransport[] | undefined })),
  };
  let cred: PublicKeyCredential | null;
  try {
    const request: CredentialRequestOptions = autofill ? { publicKey, mediation: "conditional", signal: autofill.signal } : { publicKey };
    cred = (await navigator.credentials.get(request)) as PublicKeyCredential | null;
  } catch (e) {
    throw passkeyError(e, "login");
  }
  if (!cred) throw new PasskeyCancelledError();
  const asr = cred.response as AuthenticatorAssertionResponse;
  return {
    challengeId: options.challengeId,
    credential: {
      id: cred.id,
      rawId: bufToB64url(cred.rawId),
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(asr.clientDataJSON),
        authenticatorData: bufToB64url(asr.authenticatorData),
        signature: bufToB64url(asr.signature),
        userHandle: asr.userHandle ? bufToB64url(asr.userHandle) : undefined,
      },
      clientExtensionResults: cred.getClientExtensionResults(),
    },
  };
}
